/**
 * React Query hooks for collections management
 * @module features/collections/queries
 */

import {
	apiCollectionItemSchema,
	apiCollectionSchema,
	type ApiCollection,
	type ApiCollectionItem,
	type CollectionCreateInput,
	type CollectionItemUpdateInput,
	type CollectionUpdateInput,
	type DraftCollectionInput,
} from "./schema";
import {
	useQuery,
	useMutation,
	useQueryClient,
	type UseQueryResult,
	type UseMutationResult,
} from "@tanstack/react-query";
import { apiFetch, apiUrl } from "#/utils/api";

// ============================================================================
// Types
// ============================================================================

/**
 * Filter options for querying collections
 */
export type CollectionFilters = {
	/** Predefined view for list tabs */
	view?: "outstanding" | "upcoming" | "corporate" | "drafts";
	/** @deprecated Legacy server alias for view */
	lifecycle?: "scheduled" | "rolling";
};

export type CollectionsStats = {
	outstanding?: number;
	upcoming?: number;
	corporate?: number;
	drafts?: number;
};

type CountDimension = keyof CollectionsStats;

type CountResponse = {
	[K in CountDimension]?: number;
};

// Re-export DraftCollection for convenience
export type { DraftCollectionInput };

export type { CollectionCreateInput, CollectionUpdateInput };

type ItemGraphics = {
	outside: string | null;
	inside: string | null;
};

type ItemWithGraphics = {
	graphics: ItemGraphics;
};

const parseOrderNumber = (value?: string | null): number => {
	if (!value) return Number.NEGATIVE_INFINITY;
	const digits = value.replace(/\D+/g, "");
	if (!digits) return Number.NEGATIVE_INFINITY;
	const parsed = Number.parseInt(digits, 10);
	return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
};

const sortItemsByOrderNumberDesc = <T extends { number?: string; id: string }>(items: T[]): T[] => {
	return [...items].sort((a, b) => {
		const orderNumberDiff = parseOrderNumber(b.number) - parseOrderNumber(a.number);
		if (orderNumberDiff !== 0) {
			return orderNumberDiff;
		}

		return a.id.localeCompare(b.id);
	});
};

export type SignedStorageUrlEntry = {
	input: string;
	path: string;
	gsPath: string;
	url: string;
};

export type UploadCollectionGraphicInput = {
	collectionId: string;
	side: "outside" | "inside";
	file: File;
	itemId?: string;
	sku?: string;
};

export type UploadCollectionGraphicResult = {
	success: true;
	path: string;
	gsPath: string;
	side: "outside" | "inside";
	matchedItems: string[];
};

const collectionStatusMessages: Record<number, string> = {
	401: "Unauthorized access to collection",
	403: "Access denied to collection",
	404: "Collection not found",
};

/**
 * Extracts a descriptive API error message from a failed response.
 *
 * @param response - Failed HTTP response
 * @returns Parsed error message when available
 */
async function getApiErrorMessage(response: Response): Promise<string | null> {
	try {
		const contentType = response.headers.get("content-type") ?? "";
		if (contentType.includes("application/json")) {
			const payload = (await response.json()) as {
				error?: string;
				message?: string;
				details?: unknown;
			};
			if (typeof payload.error === "string" && payload.error.length > 0) {
				return payload.error;
			}
			if (typeof payload.message === "string" && payload.message.length > 0) {
				return payload.message;
			}
		}

		const text = await response.text();
		if (text) {
			return text;
		}
	} catch {
		return null;
	}

	return null;
}

/**
 * Throws a normalized API error for non-OK HTTP responses.
 *
 * @param response - Failed HTTP response
 * @param fallback - Default error message when response has no details
 * @param byStatus - Optional status-code specific messages
 */
async function throwApiError(
	response: Response,
	fallback: string,
	byStatus?: Record<number, string>,
): Promise<never> {
	const responseMessage = await getApiErrorMessage(response);
	if (responseMessage) {
		throw new Error(responseMessage);
	}

	const statusMessage = byStatus?.[response.status];
	if (statusMessage) {
		throw new Error(statusMessage);
	}

	throw new Error(`${fallback} (status ${response.status})`);
}

// ============================================================================
// Query Keys
// ============================================================================

/**
 * Factory for generating query keys for collections
 */
export const collectionKeys = {
	all: ["collections"] as const,
	lists: () => [...collectionKeys.all, "list"] as const,
	list: (filters?: CollectionFilters) => [...collectionKeys.lists(), { filters }] as const,
	details: () => [...collectionKeys.all, "detail"] as const,
	detail: (id: string) => [...collectionKeys.details(), id] as const,
	items: (collectionId: string) => [...collectionKeys.detail(collectionId), "items"] as const,
	item: (collectionId: string, itemId: string) =>
		[...collectionKeys.items(collectionId), itemId] as const,
};

// ============================================================================
// API Functions
// ============================================================================

/**
 * Resolves any gcs:// graphics URLs to temporary signed URLs for rendering.
 */
async function resolveSignedGraphicUrls<T extends ItemWithGraphics>(items: T[]): Promise<T[]> {
	const gcsPrefix = "gs://";
	const gsPaths = new Set<string>();

	for (const item of items) {
		const outside = item.graphics.outside;
		const inside = item.graphics.inside;

		if (outside?.startsWith(gcsPrefix)) {
			gsPaths.add(outside);
		}

		if (inside?.startsWith(gcsPrefix)) {
			gsPaths.add(inside);
		}
	}

	if (gsPaths.size === 0) {
		return items;
	}

	try {
		const signedEntries = await getSignedGraphicUrlsRequest([...gsPaths]);
		const gsPathToSignedUrl = new Map(signedEntries.map((entry) => [entry.gsPath, entry.url]));

		return items.map((item) => {
			const outsidePath = item.graphics.outside ?? undefined;
			const insidePath = item.graphics.inside ?? undefined;

			return {
				...item,
				graphics: {
					outside:
						outsidePath && gsPathToSignedUrl.has(outsidePath)
							? gsPathToSignedUrl.get(outsidePath) || item.graphics.outside
							: item.graphics.outside,
					inside:
						insidePath && gsPathToSignedUrl.has(insidePath)
							? gsPathToSignedUrl.get(insidePath) || item.graphics.inside
							: item.graphics.inside,
				},
			};
		});
	} catch (error) {
		console.error("Failed to resolve signed graphics URLs:", error);
		return items;
	}
}

/**
 * Retrieves signed URLs for GCS-hosted graphics paths.
 */
export async function getSignedGraphicUrlsRequest(
	gsPaths: string[],
): Promise<SignedStorageUrlEntry[]> {
	if (gsPaths.length === 0) {
		return [];
	}

	const response = await apiFetch("/storage/signed-url", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ gsPaths }),
	});

	if (!response.ok) {
		await throwApiError(response, "Failed to fetch signed URLs for graphics");
	}

	const payload = (await response.json()) as {
		urls: SignedStorageUrlEntry[];
	};

	return payload.urls;
}

/**
 * Uploads a graphics blob and applies the resulting gs:// path to matching item graphics.
 */
export async function uploadCollectionGraphicRequest(
	input: UploadCollectionGraphicInput,
): Promise<UploadCollectionGraphicResult> {
	const formData = new FormData();
	formData.append("blob", input.file);
	formData.append("side", input.side);
	if (input.itemId) {
		formData.append("itemId", input.itemId);
	}
	if (input.sku) {
		formData.append("sku", input.sku);
	}

	const response = await apiFetch(`/collections/${input.collectionId}/items/graphics`, {
		method: "POST",
		body: formData,
	});

	if (!response.ok) {
		await throwApiError(response, "Failed to upload item graphic", collectionStatusMessages);
	}

	return (await response.json()) as UploadCollectionGraphicResult;
}

/**
 * Fetch all collections with optional filters
 */
async function fetchCollections(filters?: CollectionFilters): Promise<ApiCollection[]> {
	const params = new URLSearchParams();

	const resolvedView =
		filters?.view ??
		(filters?.lifecycle === "scheduled"
			? "upcoming"
			: filters?.lifecycle === "rolling"
				? "outstanding"
				: undefined);

	if (resolvedView) params.append("view", resolvedView);

	const queryString = params.toString();
	const url = queryString ? apiUrl(`/collections?${queryString}`) : apiUrl("/collections");

	const response = await fetch(url);
	if (!response.ok) {
		await throwApiError(response, "Failed to fetch collections");
	}
	return apiCollectionSchema.array().parse(await response.json());
}

/**
 * Fetch counts for collections tabs
 */
async function fetchCollectionsStats(
	dimensions?: Array<keyof CollectionsStats>,
): Promise<CollectionsStats> {
	const requestedDimensions: CountDimension[] =
		dimensions && dimensions.length > 0
			? dimensions
			: ["outstanding", "upcoming", "corporate", "drafts"];

	const response = await apiFetch(`/collections?countBy=${requestedDimensions.join(",")}`);
	if (!response.ok) {
		await throwApiError(response, "Failed to fetch collection stats");
	}

	const payload = (await response.json()) as CountResponse;
	const stats: CollectionsStats = {};
	for (const dimension of requestedDimensions) {
		stats[dimension] = payload[dimension] ?? 0;
	}

	return stats;
}

/**
 * Fetch a single collection by ID
 */
async function fetchCollection(id: string): Promise<ApiCollection> {
	const response = await apiFetch(`/collections/${id}`);
	if (!response.ok) {
		await throwApiError(response, "Failed to fetch collection", collectionStatusMessages);
	}
	const result = apiCollectionSchema.safeParse(await response.json());
	if (!result.success) {
		console.error(`[fetchCollection ${id}] Validation failed:`, result.error);
		throw new Error(`Invalid collection data: ${result.error.message}`);
	}

	if (!result.data.items || result.data.items.length === 0) {
		return result.data;
	}

	const resolvedItems = await resolveSignedGraphicUrls(result.data.items);

	return {
		...result.data,
		items: sortItemsByOrderNumberDesc(resolvedItems),
	};
}

/**
 * Create a new collection (posts to /v1/collections)
 */
export async function createCollectionRequest(data: CollectionCreateInput): Promise<ApiCollection> {
	const response = await apiFetch(`/collections`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(data),
	});
	if (!response.ok) {
		await throwApiError(response, "Failed to create collection");
	}
	return apiCollectionSchema.parse(await response.json());
}

/**
 * Update an existing collection
 */
export async function updateCollectionRequest(
	id: string,
	data: CollectionUpdateInput,
): Promise<{ id: string } & CollectionUpdateInput> {
	const response = await apiFetch(`/collections/${id}`, {
		method: "PATCH",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(data),
	});
	if (!response.ok) {
		await throwApiError(response, "Failed to update collection", collectionStatusMessages);
	}
	return (await response.json()) as { id: string } & CollectionUpdateInput;
}

/**
 * Delete a collection
 */
export async function deleteCollectionRequest(id: string): Promise<void> {
	const response = await apiFetch(`/collections/${id}`, {
		method: "DELETE",
	});
	if (!response.ok) {
		await throwApiError(response, "Failed to delete collection", collectionStatusMessages);
	}
}

/**
 * Fetch all items for a collection
 */
async function fetchCollectionItems(collectionId: string): Promise<ApiCollectionItem[]> {
	const response = await apiFetch(`/collections/${collectionId}/items`);
	if (!response.ok) {
		await throwApiError(response, "Failed to fetch collection items", collectionStatusMessages);
	}
	const items = apiCollectionItemSchema.array().parse(await response.json());
	const resolvedItems = await resolveSignedGraphicUrls(items);
	return sortItemsByOrderNumberDesc(resolvedItems);
}

/**
 * Fetch a single item from a collection
 */
async function fetchCollectionItem(
	collectionId: string,
	itemId: string,
): Promise<ApiCollectionItem> {
	const response = await apiFetch(`/collections/${collectionId}/items/${itemId}`);
	if (!response.ok) {
		await throwApiError(response, "Failed to fetch collection item", collectionStatusMessages);
	}
	const item = apiCollectionItemSchema.parse(await response.json());
	const [resolvedItem] = await resolveSignedGraphicUrls([item]);
	return resolvedItem;
}

/**
 * Add items to a collection
 */
export async function addCollectionItemsRequest(
	collectionId: string,
	items: Partial<ApiCollectionItem>[],
): Promise<{ success: true; created: number }> {
	if (items.length === 0) {
		return { success: true, created: 0 };
	}
	const parsedItems = apiCollectionItemSchema.array().parse(items);
	const response = await apiFetch(`/collections/${collectionId}/items`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(parsedItems),
	});
	if (!response.ok) {
		await throwApiError(response, "Failed to add items to collection", collectionStatusMessages);
	}
	const payload = (await response.json()) as { success?: true; created?: number };
	return {
		success: true,
		created: typeof payload.created === "number" ? payload.created : parsedItems.length,
	};
}

export type CollectionItemBatchUpdate = {
	itemId?: string;
	sku?: string;
	patch: CollectionItemUpdateInput;
};

/**
 * Update multiple collection items in one request.
 */
export async function batchUpdateCollectionItemsRequest(
	collectionId: string,
	updates: CollectionItemBatchUpdate[],
): Promise<{ success: true; updated: number; matchedItems: string[] }> {
	const response = await apiFetch(`/collections/${collectionId}/items`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ updates }),
	});
	if (!response.ok) {
		await throwApiError(
			response,
			"Failed to batch update collection items",
			collectionStatusMessages,
		);
	}
	return (await response.json()) as { success: true; updated: number; matchedItems: string[] };
}

/**
 * Update a collection item
 */
export async function updateCollectionItemRequest(
	collectionId: string,
	itemId: string,
	item: CollectionItemUpdateInput,
): Promise<{ success: true }> {
	const response = await apiFetch(`/collections/${collectionId}/items/${itemId}`, {
		method: "PATCH",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(item),
	});
	if (!response.ok) {
		await throwApiError(response, "Failed to update collection item", collectionStatusMessages);
	}
	return (await response.json()) as { success: true };
}

/**
 * Delete a collection item
 */
async function deleteCollectionItem(
	collectionId: string,
	itemId: string,
): Promise<{ success: true }> {
	const response = await apiFetch(`/collections/${collectionId}/items/${itemId}`, {
		method: "DELETE",
	});
	if (!response.ok) {
		await throwApiError(response, "Failed to delete collection item", collectionStatusMessages);
	}
	return (await response.json()) as { success: true };
}

/**
 * Toggle a scheduled collection draft state.
 */
export async function toggleCollectionDraftRequest(
	id: string,
): Promise<{ success: true; id: string; draft: boolean }> {
	const response = await apiFetch(`/collections/${id}`, {
		method: "POST",
	});
	if (!response.ok) {
		await throwApiError(
			response,
			"Failed to toggle collection draft state",
			collectionStatusMessages,
		);
	}
	return (await response.json()) as { success: true; id: string; draft: boolean };
}

/**
 * Trigger workflow for Shopify collection
 */
async function startCollectionWorkflow(id: string): Promise<{ ok: true; workflowRunId: string }> {
	const response = await apiFetch(`/collections/${id}/start`, {
		method: "POST",
	});
	if (!response.ok) {
		await throwApiError(response, "Failed to start collection workflow", collectionStatusMessages);
	}
	return (await response.json()) as { ok: true; workflowRunId: string };
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to fetch collections list with optional filters
 * @param filters - Optional filter parameters (client, user, complete, draft)
 * @returns Query result with collections data
 */
export function useCollections(
	filters?: CollectionFilters,
): UseQueryResult<ApiCollection[], Error> {
	return useQuery({
		queryKey: collectionKeys.list(filters),
		queryFn: () => fetchCollections(filters),
		staleTime: 10 * 60 * 1000, // 10 minutes
	});
}

/**
 * Hook to fetch collections tab counts
 * @param dimensions - Optional list of dimensions to fetch
 * @returns Query result with counts
 */
export function useCollectionsStats(
	dimensions?: Array<keyof CollectionsStats>,
): UseQueryResult<CollectionsStats, Error> {
	return useQuery({
		queryKey: [...collectionKeys.lists(), "stats", { dimensions }],
		queryFn: () => fetchCollectionsStats(dimensions),
		staleTime: 5 * 60 * 1000,
	});
}

/**
 * Hook to fetch a single collection by ID
 * @param id - Collection ID
 * @param enabled - Whether the query should run (default: true)
 * @param options - Additional query options
 * @returns Query result with collection data
 */
export function useCollection(
	id: string,
	enabled: boolean = true,
	options?: { onSuccess?: (data: ApiCollection) => void },
): UseQueryResult<ApiCollection, Error> {
	return useQuery({
		queryKey: collectionKeys.detail(id),
		queryFn: () => fetchCollection(id),
		enabled,
		staleTime: 5 * 60 * 1000, // 5 minutes
		...options,
	});
}

/**
 * Hook to fetch items for a collection
 * @param collectionId - Collection ID
 * @param enabled - Whether the query should run (default: true)
 * @returns Query result with collection items
 */
export function useCollectionItems(
	collectionId: string,
	enabled: boolean = true,
): UseQueryResult<ApiCollectionItem[], Error> {
	return useQuery({
		queryKey: collectionKeys.items(collectionId),
		queryFn: () => fetchCollectionItems(collectionId),
		enabled,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
}

/**
 * Hook to fetch a single collection item
 * @param collectionId - Collection ID
 * @param itemId - Item ID
 * @param enabled - Whether the query should run (default: true)
 * @returns Query result with collection item
 */
export function useCollectionItem(
	collectionId: string,
	itemId: string,
	enabled: boolean = true,
): UseQueryResult<ApiCollectionItem, Error> {
	return useQuery({
		queryKey: collectionKeys.item(collectionId, itemId),
		queryFn: () => fetchCollectionItem(collectionId, itemId),
		enabled,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to create a new collection
 * @returns Mutation result with create function
 */
export function useCreateCollection(): UseMutationResult<
	ApiCollection,
	Error,
	CollectionCreateInput
> {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createCollectionRequest,
		onSuccess: () => {
			// Invalidate all collection lists to refetch
			void queryClient.invalidateQueries({ queryKey: collectionKeys.lists() });
		},
	});
}

/**
 * Hook to update a collection
 * @returns Mutation result with update function
 */
export function useUpdateCollection(): UseMutationResult<
	{ id: string } & CollectionUpdateInput,
	Error,
	{ id: string; data: CollectionUpdateInput }
> {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }) => updateCollectionRequest(id, data),
		onSuccess: (_, { id }) => {
			// Invalidate to refetch full collection data
			void queryClient.invalidateQueries({ queryKey: collectionKeys.detail(id) });
			void queryClient.invalidateQueries({ queryKey: collectionKeys.lists() });
		},
	});
}

/**
 * Hook to delete a collection
 * @returns Mutation result with delete function
 */
export function useDeleteCollection(): UseMutationResult<void, Error, string> {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: deleteCollectionRequest,
		onSuccess: (_, id) => {
			// Remove from cache
			void queryClient.removeQueries({ queryKey: collectionKeys.detail(id) });
			// Invalidate lists
			void queryClient.invalidateQueries({ queryKey: collectionKeys.lists() });
		},
	});
}

/**
 * Hook to add items to a collection
 * @returns Mutation result with add function
 */
export function useAddCollectionItems(): UseMutationResult<
	{ success: true; created: number },
	Error,
	{ collectionId: string; items: Partial<ApiCollectionItem>[] }
> {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ collectionId, items }) => addCollectionItemsRequest(collectionId, items),
		onSuccess: (_, { collectionId }) => {
			// Invalidate items list to refetch
			void queryClient.invalidateQueries({ queryKey: collectionKeys.items(collectionId) });
			void queryClient.invalidateQueries({ queryKey: collectionKeys.detail(collectionId) });
		},
	});
}

/**
 * Hook to update a collection item
 * @returns Mutation result with update function
 */
export function useUpdateCollectionItem(): UseMutationResult<
	{ success: true },
	Error,
	{
		collectionId: string;
		itemId: string;
		item: CollectionItemUpdateInput;
	}
> {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ collectionId, itemId, item }) =>
			updateCollectionItemRequest(collectionId, itemId, item),
		onSuccess: (_, { collectionId, itemId }) => {
			// Invalidate item and items list
			void queryClient.invalidateQueries({ queryKey: collectionKeys.item(collectionId, itemId) });
			void queryClient.invalidateQueries({ queryKey: collectionKeys.items(collectionId) });
		},
	});
}

/**
 * Hook to delete a collection item
 * @returns Mutation result with delete function
 */
export function useDeleteCollectionItem(): UseMutationResult<
	{ success: true },
	Error,
	{ collectionId: string; itemId: string }
> {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ collectionId, itemId }) => deleteCollectionItem(collectionId, itemId),
		onSuccess: (_, { collectionId, itemId }) => {
			// Remove item from cache
			void queryClient.removeQueries({ queryKey: collectionKeys.item(collectionId, itemId) });
			// Invalidate items list and collection detail
			void queryClient.invalidateQueries({ queryKey: collectionKeys.items(collectionId) });
			void queryClient.invalidateQueries({ queryKey: collectionKeys.detail(collectionId) });
		},
	});
}

/**
 * Hook to start collection workflow (Shopify sync)
 * @returns Mutation result with start function
 */
export function useStartCollectionWorkflow(): UseMutationResult<
	{ ok: true; workflowRunId: string },
	Error,
	string
> {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: startCollectionWorkflow,
		onSuccess: (_, id) => {
			// Invalidate collection to refetch updated data
			void queryClient.invalidateQueries({ queryKey: collectionKeys.detail(id) });
			void queryClient.invalidateQueries({ queryKey: collectionKeys.items(id) });
		},
	});
}

/**
 * Hook to toggle a collection's draft state.
 * @returns Mutation result with toggle function
 */
export function useToggleCollectionDraft(): UseMutationResult<
	{ success: true; id: string; draft: boolean },
	Error,
	string
> {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: toggleCollectionDraftRequest,
		onSuccess: (_, id) => {
			void queryClient.invalidateQueries({ queryKey: collectionKeys.detail(id) });
			void queryClient.invalidateQueries({ queryKey: collectionKeys.lists() });
		},
	});
}
